import { GatewayId } from '../types/third-party-system.enums';
import { z } from 'zod';
import {
    IpgSystemId,
    IpwSystemId,
    ThirdPartySystemId } from '../types/third-party-system.enums';

function coerceNumericIdentifier(value: unknown): unknown {
    if (value === null || value === undefined) {
        return value;
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
        return value;
    }
    return numericValue;
}

const GatewayIdValues: readonly GatewayId[] = Object.values(GatewayId);
const ThirdPartySystemIdValues: readonly ThirdPartySystemId[] =
    Object.values(ThirdPartySystemId);
const NonCoreThirdPartySystemIdValues: readonly ThirdPartySystemId[] =
    ThirdPartySystemIdValues.filter(
        (systemId: ThirdPartySystemId): boolean =>
            systemId !== ThirdPartySystemId.GENERIC
    );
const NumericIpgSystemIdValues: readonly IpgSystemId[] =
    Object.values(IpgSystemId);

function isGatewayId(value: unknown): value is GatewayId {
    return GatewayIdValues.some((gatewayId: GatewayId) => gatewayId === value);
}

function isThirdPartySystemId(value: unknown): value is ThirdPartySystemId {
    return ThirdPartySystemIdValues.some(
        (systemId: ThirdPartySystemId): boolean => systemId === value
    );
}

function isNonCoreThirdPartySystemId(
    value: unknown
): value is ThirdPartySystemId {
    return NonCoreThirdPartySystemIdValues.some(
        (systemId: ThirdPartySystemId): boolean => systemId === value
    );
}

function isNumericIpgSystemId(value: unknown): value is IpgSystemId {
    return NumericIpgSystemIdValues.some(
        (systemId: IpgSystemId): boolean => systemId === value
    );
}

export const ThirdPartySystemIdSchema: z.ZodType<ThirdPartySystemId> = z.preprocess(
    coerceNumericIdentifier,
    z.custom<ThirdPartySystemId>(isThirdPartySystemId, {
        message: 'Invalid third-party system id',
    })
);

export const NonCoreThirdPartySystemIdSchema: z.ZodType<ThirdPartySystemId> = z.preprocess(
    coerceNumericIdentifier,
    z.custom<ThirdPartySystemId>(isNonCoreThirdPartySystemId, {
        message: 'Invalid non-core third-party system id',
    })
);

export const GatewayIdSchema: z.ZodType<GatewayId> = z.preprocess(
    coerceNumericIdentifier,
    z.custom<GatewayId>(isGatewayId, {
        message: 'Invalid gateway id',
    })
);

export const NumericIpgSystemIdSchema: z.ZodType<IpgSystemId> = z.preprocess(
    coerceNumericIdentifier,
    z.custom<IpgSystemId>(isNumericIpgSystemId, {
        message: 'Invalid IPG system id',
    })
);

const NumericIpwSystemIdInputSchema = z.union([
    z.literal(IpwSystemId.GENERIC),
    z.literal(IpwSystemId.DEALERTRACK),
    z.literal(IpwSystemId.PCPARTPICKER),
    z.literal('0'),
    z.literal('10001'),
    z.literal('10002'),
]);

export const NumericIpwSystemIdSchema: z.ZodType<IpwSystemId> =
    NumericIpwSystemIdInputSchema.transform((value): IpwSystemId => {
        switch (value) {
            case IpwSystemId.GENERIC:
            case '0':
                return IpwSystemId.GENERIC;
            case IpwSystemId.DEALERTRACK:
            case '10001':
                return IpwSystemId.DEALERTRACK;
            case IpwSystemId.PCPARTPICKER:
            case '10002':
                return IpwSystemId.PCPARTPICKER;
        }
    });
